import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { School, ArrowLeft, Loader2, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = "/api";

export default function SchoolApply({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    school_name: "",
    school_website: "",
    contact_name: "",
    contact_email: user?.email || "",
    contact_role: "",
    contact_phone: "",
    estimated_students: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error("Please login to apply for a school license");
      return;
    }

    // Validation
    if (!formData.school_name || !formData.contact_name || !formData.contact_email || !formData.contact_role || !formData.estimated_students) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/schools/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          estimated_students: parseInt(formData.estimated_students)
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Application submitted!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit application");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen">
        <div className="container max-w-xl mx-auto px-4 py-12">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Application Submitted!</h1>
            <p className="text-muted-foreground mb-8">
              Thank you for your interest in CodingDrills.au for your school. 
              We'll review your application and contact you within 2-3 business days.
            </p>
            <Button onClick={() => navigate("/")} data-testid="back-home-btn">
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="container max-w-xl mx-auto px-4 py-12">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6 gap-2"
          onClick={() => navigate("/subscription")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Plans
        </Button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="p-4 rounded-2xl bg-primary/10">
            <School className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Teacher Starter License</h1>
            <p className="text-muted-foreground">Free — run live drills &amp; track up to 10 student slots</p>
          </div>
        </div>

        {!user && (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400">
            Please login to apply for a school license.
          </div>
        )}

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <h2 className="font-semibold text-lg mb-4">School Information</h2>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">School Name *</label>
              <Input
                name="school_name"
                value={formData.school_name}
                onChange={handleChange}
                placeholder="e.g., Melbourne High School"
                className="mt-1"
                required
                data-testid="school-name-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">School Website</label>
              <Input
                name="school_website"
                value={formData.school_website}
                onChange={handleChange}
                placeholder="https://www.school.edu.au"
                className="mt-1"
                data-testid="school-website-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Estimated Number of Students *</label>
              <Input
                name="estimated_students"
                type="number"
                min="1"
                value={formData.estimated_students}
                onChange={handleChange}
                placeholder="e.g., 50"
                className="mt-1"
                required
                data-testid="estimated-students-input"
              />
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <h2 className="font-semibold text-lg mb-4">Contact Information</h2>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Your Name *</label>
              <Input
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder="John Smith"
                className="mt-1"
                required
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Your Role at School *</label>
              <Input
                name="contact_role"
                value={formData.contact_role}
                onChange={handleChange}
                placeholder="e.g., IT Coordinator, Teacher, Principal"
                className="mt-1"
                required
                data-testid="contact-role-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Email Address *</label>
              <Input
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="john.smith@school.edu.au"
                className="mt-1"
                required
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Phone Number</label>
              <Input
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                placeholder="+61 4XX XXX XXX"
                className="mt-1"
                data-testid="contact-phone-input"
              />
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-6">
            <label className="text-sm font-medium text-muted-foreground">Additional Information</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your school and how you plan to use CodingDrills.au..."
              className="mt-1 w-full min-h-[100px] p-3 rounded-lg bg-background border border-input resize-none"
              data-testid="message-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 text-base"
            disabled={loading || !user}
            data-testid="submit-application-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              "Submit Application"
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            We'll review your application and contact you within 2-3 business days.
          </p>
        </form>
      </div>
    </div>
  );
}
